import React, { useEffect, useRef, useState } from 'react';
import { fireGa4, fireGa4andGrx } from '../../../layouts/utils';
// import { calculateHaversineDistance } from './utils';
// eslint-disable-next-line no-unused-vars
import s from './Map.scss';

const Map = props => {
  const {
    submitCallback = () => {},
    submitGuessCallback,
    isWebView = false,
    isAppView,
    isMobile,
    maxGuessAllowed,
    errorCallback,
    onMapLoad = () => {},
    gameId = '',
    gaCallback = () => {},
  } = props;

  let { mapData = {} } = props;
  const MAX_RETRIES = 3; // Maximum number of retries
  const RETRY_DELAY = 2000; // Delay between retries for drawline
  let retryCount = 0; // Counter to track retries

  if (
    mapData &&
    mapData.stateLocationGuesser &&
    Array.isArray(mapData.stateLocationGuesser.guesses)
  ) {
    mapData = mapData.stateLocationGuesser;
  }

  const mapContainer = useRef(null);
  const map = useRef(null);
  const currentMarker = useRef(null);
  const lastGeoCodingPick = useRef(null);
  const isPopupOpenRef = useRef(false);
  const markersRef = useRef([]);
  const previousGuessesLength = useRef(
    Array.isArray(mapData.guesses) ? mapData.guesses.length : 0,
  );
  const solLong = mapData?.solution?.[0]?.guessLongitude || '';
  const solLat = mapData?.solution?.[0]?.guessLatitude || '';
  const [showInfoToast, setShowInfoToast] = useState(true);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(false);
  const [submittedGuess, setSubmittedGuess] = useState(null);

  const [guesses, setGuesses] = useState(mapData.guesses || []);
  const [bestGuessIndex, setBestGuessIndex] = useState(mapData.bestGuessIndex);
  const [solutionCoords, setSolutionCoords] = useState([solLong, solLat]);
  const [isResultView, setIsResultView] = useState(
    props.isResultView || mapData.isResultView,
  );
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const calculateBestGuessIndex = guessesArray => {
    let maxPoints = -1;
    let index = bestGuessIndex;

    guessesArray.forEach((guess, i) => {
      if (guess.pointsEarned >= maxPoints) {
        maxPoints = guess.pointsEarned;
        index = i; // Always take the latest if points are the same
      }
    });

    setBestGuessIndex(index);
  };

  const hideMapControls = () => {
    // Locate controls in the top-right area
    const controls = document.querySelectorAll('.maplibregl-ctrl-top-right');

    controls.forEach(control => {
      control.style.display = 'none'; // Hide the controls
    });
  };

  const addDottedLine = () => {
    if (
      !map.current ||
      !guesses ||
      !solutionCoords ||
      !Array.isArray(guesses) ||
      typeof solutionCoords[0] === 'undefined' ||
      solutionCoords[0] === '' ||
      typeof bestGuessIndex === 'undefined' ||
      !markersRef.current
    ) {
      return;
    }

    const solutionMarker = new window.maptilersdk.Marker({
      color: 'green',
      className: 'resultMarker',
    })
      .setLngLat(solutionCoords)
      .addTo(map.current);
    markersRef.current.push(solutionMarker);

    // Fit all markers within view
    const bounds = new window.maptilersdk.LngLatBounds();
    markersRef.current.forEach(marker => bounds.extend(marker.getLngLat()));
    map.current.fitBounds(bounds, {
      padding: 120,
      maxZoom: isResultView ? 20 : 5,
    });

    let bestGuessCoords;

    guesses.forEach((guess, index) => {
      if (bestGuessIndex === index) {
        bestGuessCoords = [guess.guessLongitude, guess.guessLatitude];
      }
    });

    if (!map.current.isStyleLoaded()) {
      if (retryCount < MAX_RETRIES) {
        retryCount += 1;
        setTimeout(addDottedLine, RETRY_DELAY);
      }
      return;
    }

    const lineId = `guessLine`;

    if (map.current.getLayer(lineId)) {
      map.current.removeLayer(lineId);
    }
    if (map.current.getSource(lineId)) {
      map.current.removeSource(lineId);
    }

    map.current.addSource(lineId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [bestGuessCoords, solutionCoords],
        },
      },
    });

    map.current.addLayer({
      id: lineId,
      type: 'line',
      source: lineId,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#000000',
        'line-width': 2,
        'line-dasharray': [2, 4],
      },
    });
  };

  const initMap = async () => {
    if (!window.maptilersdk) {
      console.error('MapTiler SDK is not loaded');
      if (typeof errorCallback === 'function') {
        errorCallback();
      } else {
        setShowErrorMessage(true);
      }
      return;
    }

    if (map.current) {
      map.current.remove();
    }

    // Configure the API key
    window.maptilersdk.config.apiKey = 'oTg4niESg5a1ujAuSCRn'; // local -- L5ueCuJZ3hNLUdAr7Zu9

    // Initialize the Map
    map.current = new window.maptilersdk.Map({
      container: mapContainer.current,
      style: 'd0440b72-f63b-43f1-890b-cdaf366c8cbe',
      center: [78.9629, 20.5937],
      zoom: 3,
      //center: [16.3, 49.2],
      // zoom: 7,
      // geolocate: window.maptilersdk.GeolocationType.COUNTRY,
      attributionControl: false, // Removes the logo and attribution
      //interactive: !isResultView,
      cooperativeGestures: isResultView,
      locale: {
        'CooperativeGesturesHandler.WindowsHelpText':
          'Use Ctrl + scroll to zoom the map',
        'CooperativeGesturesHandler.MacHelpText':
          'Use ⌘ + scroll to zoom the map',
        'CooperativeGesturesHandler.MobileHelpText':
          'Use two fingers to move the map',
      },
    });

    await map.current.onReadyAsync();

    if (isAppView) {
      onMapLoad();
    }

    if (
      window?.maptilersdkMaptilerGeocoder &&
      window.maptilersdkMaptilerGeocoder?.GeocodingControl &&
      !isResultView
    ) {
      const geocodingControl = new window.maptilersdkMaptilerGeocoder.GeocodingControl(
        {
          class: 'customeSearchToi',
          placeholder: 'Search Location',
          marker: false,
          clearOnBlur: true,
        },
      );

      geocodingControl.addEventListener('pick', e => {
        if (e && e.detail) {
          const currentPick = JSON.stringify(e.detail.center);
          if (
            (!lastGeoCodingPick.current ||
              currentPick !== lastGeoCodingPick.current) &&
            Array.isArray(e.detail.center) &&
            e.detail.center.length === 2
          ) {
            lastGeoCodingPick.current = currentPick;
            if (!isPopupOpenRef.current) {
              // Remove the current marker
              if (currentMarker.current) {
                currentMarker.current.remove();
              }
              const lat = e.detail.center[1];
              const lng = e.detail.center[0];

              const markerDiv = document.createElement('div');
              markerDiv.className = 'customMarker';

              markerDiv.innerHTML = `
              <div class="markerContent">
                <div class="locationMarker"><div class="markerNumber">${previousGuessesLength.current +
                  1}</div></div>
              </div>
            `;

              const marker = new window.maptilersdk.Marker({
                element: markerDiv,
                draggable: true,
              })
                .setLngLat([lng, lat])
                .addTo(map.current);

              markersRef.current.push(marker);
              // Update the ref and state
              currentMarker.current = marker;
              const clickedPoint = { lng, lat };

              setSelectedMarker(clickedPoint);
              setShowInfoToast(false);
            }
          }
        }
      });
      map.current.addControl(geocodingControl, 'top-left');
    }

    // Initial Markers
    if (Array.isArray(guesses)) {
      guesses.forEach((guess, index) => {
        const markerDiv = document.createElement('div');
        markerDiv.className = 'customMarker';

        markerDiv.innerHTML = `
        <div class="markerContent">

          <div class="locationMarker"><div class="markerNumber">${index +
            1}</div></div>
          <div class="markerPoints">${
            guess.distanceFromLocationInKms
              ? `${guess.distanceFromLocationInKms} Kms away`
              : ''
          } ${bestGuessIndex === index ? '<i class="starIcon"></i>' : ''}</div>
        </div>
      `;
        const marker = new window.maptilersdk.Marker({ element: markerDiv })
          .setLngLat([guess.guessLongitude, guess.guessLatitude])
          .addTo(map.current);
        markersRef.current.push(marker);
      });
    }

    if (!isResultView) {
      // Event listener for map clicks
      map.current.on('click', e => {
        if (!isPopupOpenRef.current) {
          const { lng, lat } = e.lngLat;

          // Remove the current marker
          if (currentMarker.current) {
            currentMarker.current.remove();
          }

          const markerDiv = document.createElement('div');
          markerDiv.className = 'customMarker';

          // Add the dynamic text inside the marker
          markerDiv.innerHTML = `
          <div class="markerContent">
            <div class="locationMarker"><div class="markerNumber">${previousGuessesLength.current +
              1}</div></div>
          </div>
        `;

          const marker = new window.maptilersdk.Marker({
            element: markerDiv,
            draggable: true,
          })
            .setLngLat([lng, lat])
            .addTo(map.current);

          markersRef.current.push(marker);
          // Update the ref and state
          currentMarker.current = marker;
          const clickedPoint = { lng, lat };

          setSelectedMarker(clickedPoint);
          setShowInfoToast(false);
          // setIsMarkerAdded(true);
        }
      });
    }

    //-- New Code -- //
    if (isResultView && isResultView === true) {
      hideMapControls();
      if (guesses && guesses.length > 0) {
        // Fit all markers within view
        const bounds = new window.maptilersdk.LngLatBounds();
        markersRef.current.forEach(marker => bounds.extend(marker.getLngLat()));
        map.current.fitBounds(bounds, {
          padding: 120,
          maxZoom: isResultView ? 20 : 5,
        });
      }

      setTimeout(() => {
        addDottedLine();
      }, 3000);
    }
  };

  const refreshMarkers = () => {
    // initMap();
    if (map.current) {
      // Remove all existing markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = []; // Clear the array

      // Add markers for all guesses in the updated array
      // Add new markers for all guesses
      guesses.forEach((guess, index) => {
        const markerDiv = document.createElement('div');
        markerDiv.className = 'customMarker';

        markerDiv.innerHTML = `
        <div class="markerContent">
          <div class="locationMarker"><div class="markerNumber">${index +
            1}</div></div>
          <div class="markerPoints">${
            guess.distanceFromLocationInKms
              ? `${guess.distanceFromLocationInKms} Kms away`
              : ''
          } ${bestGuessIndex === index ? '<i class="starIcon"></i>' : ''}</div>
        </div>
      `;

        const marker = new window.maptilersdk.Marker({ element: markerDiv })
          .setLngLat([guess.guessLongitude, guess.guessLatitude])
          .addTo(map.current);
        markersRef.current.push(marker);
      });

      //-- New Code --//
      if (isResultView && isResultView === true) {
        setTimeout(() => {
          addDottedLine();
        }, 3000);
      }
      if (guesses && guesses.length > 0) {
        // Fit all markers within view
        const bounds = new window.maptilersdk.LngLatBounds();
        markersRef.current.forEach(marker => bounds.extend(marker.getLngLat()));
        map.current.fitBounds(bounds, {
          padding: 120,
          maxZoom: isResultView ? 20 : 5,
        });
      }
    }
  };

  const refreshMap = () => {
    refreshMarkers();
    // setDistance(null);
    setIsPopupOpen(false);
    setSelectedMarker(false);
    setSubmittedGuess(null);
    // Clear the current marker
    if (currentMarker.current) {
      currentMarker.current = null;
    }
  };

  useEffect(
    () => {
      isPopupOpenRef.current = isPopupOpen;
    },
    [isPopupOpen],
  );

  useEffect(
    () => {
      // initialiseUnifiedAppLoginHandlers();

      refreshMarkers();
      // return () => map.current.remove(); // Cleanup map on unmount
    },
    [bestGuessIndex],
  );

  useEffect(() => {
    // initialiseUnifiedAppLoginHandlers();

    initMap();
    // return () => map.current.remove(); // Cleanup map on unmount
  }, []);

  useEffect(
    () => {
      if (isAppView && map.current) {
        refreshMap();
      }
    },
    [guesses],
  );

  useEffect(
    () => {
      if (mapData && Array.isArray(mapData.guesses)) {
        const newGuessesLength = mapData.guesses.length;
        if (isWebView) {
          calculateBestGuessIndex(mapData.guesses);
        }
        if (previousGuessesLength.current !== newGuessesLength) {
          if (isAppView && mapData.bestGuessIndex !== undefined) {
            setBestGuessIndex(mapData.bestGuessIndex);
          }
          setGuesses(mapData.guesses);
          previousGuessesLength.current = newGuessesLength;
          setIsResultView(props.isResultView || mapData.isResultView);
          setSolutionCoords([
            mapData?.solution?.[0]?.guessLongitude,
            mapData?.solution?.[0]?.guessLatitude,
          ]);
          setSubmittedGuess(mapData.guesses[mapData.guesses.length - 1]);
        }
      }
    },
    [mapData],
  );

  const submitGuess = () => {
    if (selectedMarker) {
      const { lat, lng } = selectedMarker;
      // const calculatedDistance = calculateHaversineDistance(
      //   indiaCenter,
      //   selectedMarker,
      // );
      // setDistance(calculatedDistance);

      // todo: instead of pushing new guess and updating state of guess, use a useeffect for whenever we get updated mapdata
      // const newGuess = {
      //   guessLatitude: lat,
      //   guessLongitude: lng,
      //   distanceFromLocationInKms: calculatedDistance,
      //   pointsEarned: 200,
      // };
      // setGuesses(prevGuesses => [...prevGuesses, newGuess]); // Add the new guess to the guesses array

      if (typeof submitGuessCallback === 'function') {
        submitGuessCallback(lat, lng);
      }

      // setSubmittedGuess(newGuess);
      setIsPopupOpen(true);
      fireGa4andGrx(
        'submit_guess',
        'user_click_games',
        'gameplay',
        'location_guesser',
        isMobile,
        gameId,
        previousGuessesLength.current ? previousGuessesLength.current + 1 : 1,
      );
      // if (isAppView) {
      //   refreshMap();
      // }
    }
  };

  const guessCtaText = isAppView ? mapData.ctaText : 'Submit Guess';
  return (
    <div
      className={`${s.mapContainer} ${isAppView ? s.appView : ''} ${
        !isAppView ? s.mapContainerWrap : ''
      } ${isPopupOpen && isMobile ? s.fullHeight : ''}`}
    >
      <div className={s.mapInnerArea}>
        <div ref={mapContainer} className={s.map} />

        {/* <div className={s.searchForm}>
        <div className={s.inputField}>
          <input type="text" placeholder="Search Location" />
          <i className={`${s.locationGuesserSprite} ${s.searchIcon}`} />
        </div>
      </div> */}

        {isPopupOpen &&
          submittedGuess &&
          isWebView && (
            <div className={`${s.distanceInfo} distanceInfoPopup`}>
              <div className={s.top}>
                <div className={s.guessTotal}>
                  <span className={s.txt}>Guess</span>{' '}
                  <strong>{guesses.length}</strong>
                  <span className={s.total}>{`/${maxGuessAllowed}`}</span>
                </div>
                <div className={s.points}>
                  <strong>{`${submittedGuess.pointsEarned} Points`}</strong>{' '}
                  {bestGuessIndex === guesses.length - 1 ? (
                    <i className={`${s.locationGuesserSprite} ${s.starIcon}`} />
                  ) : null}
                </div>
              </div>
              <div className={s.mid}>
                <ul className={s.list}>
                  <li>
                    {submittedGuess.pointsEarned === 1000
                      ? 'You are exactly at your location'
                      : `You are ${
                          submittedGuess.distanceFromLocationInKms
                        } Kms away from the location.`}
                  </li>
                  <li>
                    {submittedGuess.pointsEarned === 1000 &&
                      'Amazing! You nailed it - click "Submit Game" to finish.'}
                    {submittedGuess.pointsEarned !== 1000 && (
                      <>
                        {guesses.length < maxGuessAllowed
                          ? `You still have ${maxGuessAllowed -
                              guesses.length} guesses remaining to improve your score.`
                          : 'You have exhausted all your guesses.'}
                      </>
                    )}
                  </li>
                </ul>
                <div className={s.note}>
                  <div className={s.left}>Note:</div>
                  <div className={s.right}>
                    Only the score of your closest guess will be considered.
                  </div>
                </div>
              </div>
              <div className={s.bottom}>
                {submittedGuess.pointsEarned !== 1000 &&
                guesses.length < maxGuessAllowed ? (
                  <>
                    <button
                      onClick={() => {
                        refreshMap();
                        fireGa4andGrx(
                          'take_another_guess',
                          'user_click_games',
                          'gameplay',
                          'location_guesser',
                          isMobile,
                          gameId,
                          previousGuessesLength.current,
                        );
                      }}
                      className={`${s.primaryBtn} ${s.shadow}`}
                    >
                      Take another guess
                    </button>
                    <button
                      className={s.endGame}
                      onClick={() => {
                        submitCallback(guesses);
                        fireGa4andGrx(
                          'submit_game',
                          'user_click_games',
                          'gameplay',
                          'location_guesser',
                          isMobile,
                          gameId,
                          previousGuessesLength.current,
                          '',
                          'end',
                        );
                      }}
                    >
                      End Game
                    </button>
                  </>
                ) : (
                  <button
                    className={s.primaryBtn}
                    onClick={() => {
                      submitCallback(guesses);
                      fireGa4andGrx(
                        'submit_game',
                        'user_click_games',
                        'gameplay',
                        'location_guesser',
                        isMobile,
                        gameId,
                        '',
                        '',
                        'submit',
                      );
                    }}
                  >
                    Submit Game
                  </button>
                )}
              </div>
            </div>
          )}
      </div>
      {showErrorMessage &&
        !isResultView && (
          <div className={s.errorContent}>
            <figure>
              <img
                src="https://static.toiimg.com/photo/116043070.cms"
                alt="error vector"
              />
              {/* for dark theme: */}
              {/* <img
                src="https://static.toiimg.com/photo/116043067.cms"
                alt="error vector"
              /> */}
            </figure>
            <div className={s.error}>
              <strong>Looks like something went wrong!</strong>
              <span>Please try to restart the game</span>
            </div>
          </div>
        )}
      {showErrorMessage &&
        isResultView && (
          <div className={s.errorContentCongrats}>
            <div className={s.errorMsg}>
              <strong>
                Preparing your map, taking longer than usual time!{' '}
              </strong>
              <span>Please check after sometime</span>
            </div>
          </div>
        )}
      {!isResultView &&
        !submittedGuess &&
        Array.isArray(guesses) &&
        (!maxGuessAllowed ||
          (maxGuessAllowed && guesses.length <= maxGuessAllowed)) && (
          <div className={s.bottomCta}>
            {showInfoToast && (
              <div className={s.infoCta}>
                Zoom-in the map and tap to select a location
              </div>
            )}
            {guessCtaText && (
              <div className={s.guessBtnWrap}>
                <button
                  className={`${s.primaryBtn} ${s.shadow} ${s.guessBtn} ${
                    !selectedMarker ? s.disabled : ''
                  }`}
                  disabled={!selectedMarker}
                  onClick={submitGuess}
                >
                  {guessCtaText}
                </button>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default Map;
